import { useEffect, useRef, useState } from "react";
import type {
  CmmdMember,
  AddCmmdProjectMemberParams,
  CmmdProjectAgentMember,
  CmmdProjectAgentInstance,
  CmmdProjectAgentMemberConsolidationPreview,
  CmmdProjectAgentMemberConsolidationResult,
  CmmdProjectLeadEvents,
  CreateCmmdProjectAgentMemberParams,
  CmmdProject,
  CmmdProjectRepository,
  CmmdProjectUpdateStatus,
  CmmdProjectUpdateView,
  CmmdWorkspace,
} from "@cmmd-forge/contracts";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { type ForgeExecutionTarget } from "./forgeExecutionTarget";
import {
  resolveProjectExecutionStatus,
  type ProjectExecutionStatus,
} from "./projectExecutionStatus";
import { applyProjectSettingsPatch, type CmmdProjectSettingsPatch } from "./projectSettingsPatch";
import { enrichProjectLeadAvatar } from "./resolveProjectLeadAvatar";
import { cmmdWorkIntakeResource } from "~/cmmd/workIntakeResource";

// Projects owns its local planning-data errors. A stale CMMD REST JWT should
// not log the user out of the primary Forge session.
const projectsApiClient = createCmmdApiClient();
const PROJECT_UPDATES_PAGE_SIZE = 10;
const AGENT_EVENT_TAIL_SIZE = 200;

async function listLatestProjectAgentEvents(
  projectId: string,
): Promise<CmmdProjectLeadEvents["events"]> {
  const page = await projectsApiClient.listProjectLeadEvents(projectId, {
    latest: true,
    limit: AGENT_EVENT_TAIL_SIZE,
  });
  return page.events;
}

export interface CmmdProjectHomeState {
  readonly project: CmmdProject | null;
  readonly workspace: CmmdWorkspace | null;
  readonly repository: CmmdProjectRepository | null;
  readonly members: ReadonlyArray<CmmdMember>;
  readonly workspaceMembers: ReadonlyArray<CmmdMember>;
  readonly workspaceMembersLoading: boolean;
  readonly workspaceMembersError: string | null;
  readonly agentMembers: ReadonlyArray<CmmdProjectAgentMember>;
  readonly agentMembersLoading: boolean;
  readonly agentMembersError: string | null;
  readonly agentEvents: CmmdProjectLeadEvents["events"];
  readonly agentEventsLoading: boolean;
  readonly agentEventsError: string | null;
  readonly updates: ReadonlyArray<CmmdProjectUpdateView>;
  readonly updatesTotal: number;
  readonly updatesHasMore: boolean;
  readonly updatesLoading: boolean;
  readonly updatesError: string | null;
  readonly forgeTarget: ForgeExecutionTarget | null;
  /** Honest execution status: distinguishes a repo-less project from a failed link fetch. */
  readonly executionStatus: ProjectExecutionStatus;
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
  readonly addProjectMember: (input: AddCmmdProjectMemberParams) => Promise<void>;
  readonly createAgentMember: (
    input: Omit<CreateCmmdProjectAgentMemberParams, "idempotencyKey">,
  ) => Promise<void>;
  readonly renameAgentMember: (memberId: number, displayName: string) => Promise<void>;
  readonly retireAgentMember: (memberId: number) => Promise<void>;
  readonly updateAgentMemberAvatar: (
    memberId: number,
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
  readonly listAgentMemberInstances: (
    memberId: number,
  ) => Promise<ReadonlyArray<CmmdProjectAgentInstance>>;
  readonly previewAgentMemberConsolidation: () => Promise<CmmdProjectAgentMemberConsolidationPreview>;
  readonly applyAgentMemberConsolidation: (
    expectedPlanHash: string,
  ) => Promise<CmmdProjectAgentMemberConsolidationResult>;
  /** Save project-level settings (name, status, priority, lead, target date). */
  readonly updateSettings: (patch: CmmdProjectSettingsPatch) => Promise<void>;
  /** Save the project description inline, then refresh. */
  readonly updateDescription: (description: string) => Promise<void>;
  /** Save the CMMD-owned work-item namespace used by task keys such as FOR-1412. */
  readonly updateWorkItemPrefix: (workItemPrefix: string | null) => Promise<void>;
  /** Attach an existing GitHub repository without creating a duplicate repo. */
  readonly updateRepository: (githubRepoUrl: string) => Promise<void>;
  /** Post a project status update, then refresh the feed. */
  readonly addUpdate: (input: { status: CmmdProjectUpdateStatus; body: string }) => Promise<void>;
  /** Edit one of your own project status updates. */
  readonly updateUpdate: (
    updateId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ) => Promise<void>;
  /** Delete one of your own project status updates, then refresh the canonical first page. */
  readonly deleteUpdate: (updateId: string) => Promise<void>;
  /** Append the next canonical page without duplicating already-rendered updates. */
  readonly loadMoreUpdates: () => Promise<void>;
}

/**
 * Fetches all data needed for the CMMD project home page via the JWT-backed
 * CMMD REST API only — never the MCP work-intake "sync" (that path is for the
 * agent, and 503s take the whole page down when CMMD's MCP connection is down).
 * Project metadata comes from `getProject`; the workspace is derived from the
 * project's own fields; repo + members are best-effort enrichment.
 */
export function useCmmdProjectHome(projectId: string): CmmdProjectHomeState {
  const [project, setProject] = useState<CmmdProject | null>(null);
  const [workspace, setWorkspace] = useState<CmmdWorkspace | null>(null);
  const [repository, setRepository] = useState<CmmdProjectRepository | null>(null);
  const [members, setMembers] = useState<ReadonlyArray<CmmdMember>>([]);
  const [workspaceMembers, setWorkspaceMembers] = useState<ReadonlyArray<CmmdMember>>([]);
  const [workspaceMembersLoading, setWorkspaceMembersLoading] = useState(true);
  const [workspaceMembersError, setWorkspaceMembersError] = useState<string | null>(null);
  const [agentMembers, setAgentMembers] = useState<ReadonlyArray<CmmdProjectAgentMember>>([]);
  const [agentMembersLoading, setAgentMembersLoading] = useState(true);
  const [agentMembersError, setAgentMembersError] = useState<string | null>(null);
  const [agentEvents, setAgentEvents] = useState<CmmdProjectLeadEvents["events"]>([]);
  const [agentEventsLoading, setAgentEventsLoading] = useState(true);
  const [agentEventsError, setAgentEventsError] = useState<string | null>(null);
  const [updates, setUpdates] = useState<ReadonlyArray<CmmdProjectUpdateView>>([]);
  const [updatesTotal, setUpdatesTotal] = useState(0);
  const [updatesPage, setUpdatesPage] = useState(1);
  const [updatesHasMore, setUpdatesHasMore] = useState(false);
  const [updatesLoading, setUpdatesLoading] = useState(true);
  const [updatesError, setUpdatesError] = useState<string | null>(null);
  const [forgeTarget, setForgeTarget] = useState<ForgeExecutionTarget | null>(null);
  const [executionStatus, setExecutionStatus] = useState<ProjectExecutionStatus>("no_repo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const pendingAgentMemberCreateRef = useRef<{
    readonly fingerprint: string;
    readonly idempotencyKey: string;
  } | null>(null);
  const pendingAgentMemberRenameRef = useRef(new Map<string, string>());
  const pendingAgentMemberRetireRef = useRef(new Map<number, string>());
  const pendingAgentMemberConsolidationRef = useRef(new Map<string, string>());
  const agentMembersReadGenerationRef = useRef(0);
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;

  const reload = () => setNonce((n) => n + 1);
  const addProjectMember: CmmdProjectHomeState["addProjectMember"] = async (input) => {
    await projectsApiClient.addProjectMember(projectId, input);
    const canonicalMembers = await projectsApiClient.getProjectMembers(projectId);
    if (currentProjectIdRef.current === projectId) setMembers(canonicalMembers);
  };
  const readBackAgentMembers = async (expectedProjectId = projectId) => {
    if (currentProjectIdRef.current !== expectedProjectId) return;
    const generation = ++agentMembersReadGenerationRef.current;
    setAgentMembersLoading(true);
    setAgentEventsLoading(true);
    setAgentMembersError(null);
    setAgentEventsError(null);
    const isCurrentRead = () =>
      currentProjectIdRef.current === expectedProjectId &&
      agentMembersReadGenerationRef.current === generation;
    const roster = projectsApiClient.listProjectAgentMembers(expectedProjectId).then(
      (canonical) => {
        if (!isCurrentRead()) return;
        setAgentMembers(canonical);
        setAgentMembersLoading(false);
      },
      (cause: unknown) => {
        if (!isCurrentRead()) return;
        setAgentMembersError(
          cause instanceof Error ? cause.message : "Failed to load Agent Members.",
        );
        setAgentMembersLoading(false);
      },
    );
    void listLatestProjectAgentEvents(expectedProjectId).then(
      (activity) => {
        if (!isCurrentRead()) return;
        setAgentEvents(activity);
        setAgentEventsLoading(false);
      },
      (cause: unknown) => {
        if (!isCurrentRead()) return;
        setAgentEventsError(
          cause instanceof Error ? cause.message : "Agent activity history is unavailable.",
        );
        setAgentEventsLoading(false);
      },
    );
    await roster;
  };

  const createAgentMember: CmmdProjectHomeState["createAgentMember"] = async (input) => {
    const fingerprint = JSON.stringify({ projectId, input });
    const pending = pendingAgentMemberCreateRef.current;
    const idempotencyKey =
      pending?.fingerprint === fingerprint ? pending.idempotencyKey : crypto.randomUUID();
    const retryIdentity = { fingerprint, idempotencyKey };
    pendingAgentMemberCreateRef.current = retryIdentity;
    await projectsApiClient.createProjectAgentMember(projectId, {
      ...input,
      idempotencyKey,
    });
    if (
      pendingAgentMemberCreateRef.current?.fingerprint === retryIdentity.fingerprint &&
      pendingAgentMemberCreateRef.current.idempotencyKey === retryIdentity.idempotencyKey
    ) {
      pendingAgentMemberCreateRef.current = null;
    }
    await readBackAgentMembers(projectId);
  };

  const renameAgentMember: CmmdProjectHomeState["renameAgentMember"] = async (
    memberId,
    displayName,
  ) => {
    const retryIdentity = `${projectId}:${memberId}:${displayName}`;
    const idempotencyKey =
      pendingAgentMemberRenameRef.current.get(retryIdentity) ?? crypto.randomUUID();
    pendingAgentMemberRenameRef.current.set(retryIdentity, idempotencyKey);
    await projectsApiClient.renameProjectAgentMember(projectId, memberId, {
      displayName,
      idempotencyKey,
    });
    pendingAgentMemberRenameRef.current.delete(retryIdentity);
    await readBackAgentMembers();
  };

  const retireAgentMember: CmmdProjectHomeState["retireAgentMember"] = async (memberId) => {
    const idempotencyKey = pendingAgentMemberRetireRef.current.get(memberId) ?? crypto.randomUUID();
    pendingAgentMemberRetireRef.current.set(memberId, idempotencyKey);
    await projectsApiClient.retireProjectAgentMember(projectId, memberId, {
      idempotencyKey,
    });
    pendingAgentMemberRetireRef.current.delete(memberId);
    await readBackAgentMembers();
  };

  const updateAgentMemberAvatar: CmmdProjectHomeState["updateAgentMemberAvatar"] = async (
    memberId,
    avatar,
  ) => {
    await projectsApiClient.updateProjectAgentMemberAvatar(projectId, memberId, {
      avatar,
      idempotencyKey: crypto.randomUUID(),
    });
    await readBackAgentMembers();
  };

  const listAgentMemberInstances: CmmdProjectHomeState["listAgentMemberInstances"] = (memberId) =>
    projectsApiClient.listProjectAgentMemberInstances(projectId, memberId);

  const previewAgentMemberConsolidation: CmmdProjectHomeState["previewAgentMemberConsolidation"] =
    () => projectsApiClient.previewProjectAgentMemberConsolidation(projectId);

  const applyAgentMemberConsolidation: CmmdProjectHomeState["applyAgentMemberConsolidation"] =
    async (expectedPlanHash) => {
      const idempotencyKey =
        pendingAgentMemberConsolidationRef.current.get(expectedPlanHash) ?? crypto.randomUUID();
      pendingAgentMemberConsolidationRef.current.set(expectedPlanHash, idempotencyKey);
      const result = await projectsApiClient.applyProjectAgentMemberConsolidation(projectId, {
        expectedPlanHash,
        idempotencyKey,
      });
      pendingAgentMemberConsolidationRef.current.delete(expectedPlanHash);
      await readBackAgentMembers(projectId);
      return result;
    };

  const refreshUpdates = async () => {
    setUpdatesLoading(true);
    setUpdatesError(null);
    try {
      const page = await projectsApiClient.getProjectUpdates(projectId, {
        page: 1,
        limit: PROJECT_UPDATES_PAGE_SIZE,
      });
      setUpdates(page.updates);
      setUpdatesTotal(page.pagination.total);
      setUpdatesPage(page.pagination.page);
      setUpdatesHasMore(page.pagination.hasMore);
    } catch (cause) {
      setUpdatesError(cause instanceof Error ? cause.message : "Failed to load project updates.");
    } finally {
      setUpdatesLoading(false);
    }
  };

  const updateSettings = async (patch: CmmdProjectSettingsPatch) => {
    await projectsApiClient.updateProject(projectId, patch);
    if (patch.status !== undefined) {
      cmmdWorkIntakeResource.patchProject(projectId, { status: patch.status });
    }
    setProject((previous) =>
      previous ? applyProjectSettingsPatch(previous, patch, members) : previous,
    );
  };

  const updateDescription = async (description: string) => {
    await projectsApiClient.updateProject(projectId, { description });
    setProject((prev) => (prev ? { ...prev, description } : prev));
  };

  const updateWorkItemPrefix = async (workItemPrefix: string | null) => {
    await projectsApiClient.updateProject(projectId, { workItemPrefix });
    setProject((previous) => (previous ? { ...previous, workItemPrefix } : previous));
  };

  const updateRepository = async (githubRepoUrl: string) => {
    await projectsApiClient.updateProject(projectId, { githubRepoUrl });
    const [canonicalProject, execution] = await Promise.all([
      projectsApiClient.getProject(projectId),
      resolveProjectExecutionStatus(projectId, (id) => projectsApiClient.getProjectRepository(id)),
    ]);
    if (canonicalProject.githubRepoUrl?.toLowerCase() !== githubRepoUrl.toLowerCase()) {
      throw new Error("Repository link was not confirmed by CMMD. Refresh and try again.");
    }
    setProject(canonicalProject);
    setRepository(execution.repository);
    setForgeTarget(execution.forgeTarget);
    setExecutionStatus(execution.status);
  };

  const addUpdate = async (input: { status: CmmdProjectUpdateStatus; body: string }) => {
    const created = await projectsApiClient.createProjectUpdate(projectId, input);
    setUpdates((previous) => [created, ...previous.filter((update) => update.id !== created.id)]);
    setUpdatesTotal((previous) => previous + 1);
    await refreshUpdates();
  };

  const updateUpdate = async (
    updateId: string,
    input: { status: CmmdProjectUpdateStatus; body: string },
  ) => {
    const updated = await projectsApiClient.updateProjectUpdate(projectId, updateId, input);
    setUpdates((previous) =>
      previous.map((candidate) => (candidate.id === updateId ? updated : candidate)),
    );
    await refreshUpdates();
  };

  const deleteUpdate = async (updateId: string) => {
    await projectsApiClient.deleteProjectUpdate(projectId, updateId);
    setUpdates((previous) => previous.filter((update) => update.id !== updateId));
    setUpdatesTotal((previous) => Math.max(0, previous - 1));
    await refreshUpdates();
  };

  const loadMoreUpdates = async () => {
    if (updatesLoading || !updatesHasMore) return;
    setUpdatesLoading(true);
    setUpdatesError(null);
    try {
      const next = await projectsApiClient.getProjectUpdates(projectId, {
        page: updatesPage + 1,
        limit: PROJECT_UPDATES_PAGE_SIZE,
      });
      setUpdates((previous) => {
        const existingIds = new Set(previous.map((update) => update.id));
        const uniqueNext = next.updates.filter((update) => !existingIds.has(update.id));
        return [...previous, ...uniqueNext];
      });
      setUpdatesTotal(next.pagination.total);
      setUpdatesPage(next.pagination.page);
      setUpdatesHasMore(next.pagination.hasMore);
    } catch (cause) {
      setUpdatesError(cause instanceof Error ? cause.message : "Failed to load older updates.");
    } finally {
      setUpdatesLoading(false);
    }
  };

  useEffect(() => {
    const agentMembersReadGeneration = ++agentMembersReadGenerationRef.current;
    pendingAgentMemberCreateRef.current = null;
    pendingAgentMemberRenameRef.current.clear();
    pendingAgentMemberRetireRef.current.clear();
    pendingAgentMemberConsolidationRef.current.clear();
    let active = true;
    setLoading(true);
    setError(null);
    setProject(null);
    setWorkspace(null);
    setRepository(null);
    setMembers([]);
    setWorkspaceMembers([]);
    setWorkspaceMembersLoading(true);
    setWorkspaceMembersError(null);
    setAgentMembers([]);
    setAgentMembersLoading(true);
    setAgentMembersError(null);
    setAgentEvents([]);
    setAgentEventsLoading(true);
    setAgentEventsError(null);
    setUpdates([]);
    setUpdatesTotal(0);
    setUpdatesPage(1);
    setUpdatesHasMore(false);
    setUpdatesLoading(true);
    setUpdatesError(null);
    setForgeTarget(null);
    setExecutionStatus("no_repo");

    const projectFetch = projectsApiClient.getProject(projectId).then((project) => {
      const workspace: CmmdWorkspace | null =
        project.workspaceId && project.workspaceName
          ? {
              id: project.workspaceId,
              name: project.workspaceName,
              slug: null,
              color: null,
              logoUrl: null,
              iconUrl: null,
            }
          : null;
      return { project, workspace };
    });
    // Shared resolver — never silently mask a link-fetch error as "no repo".
    const executionFetch = resolveProjectExecutionStatus(projectId, (id) =>
      projectsApiClient.getProjectRepository(id),
    );
    const membersFetch = projectsApiClient
      .getProjectMembers(projectId)
      .catch(() => [] as CmmdMember[]);
    const workspaceMembersFetch = projectsApiClient.listWorkspaceMembers();
    const agentMembersFetch = projectsApiClient.listProjectAgentMembers(projectId);
    const agentEventsFetch = listLatestProjectAgentEvents(projectId);
    const updatesFetch = projectsApiClient.getProjectUpdates(projectId, {
      page: 1,
      limit: PROJECT_UPDATES_PAGE_SIZE,
    });

    Promise.all([projectFetch, executionFetch, membersFetch])
      .then(([planning, execution, mems]) => {
        if (!active) return;
        setProject(enrichProjectLeadAvatar(planning.project, mems));
        setWorkspace(planning.workspace);
        setRepository(execution.repository);
        setMembers(mems);
        setForgeTarget(execution.forgeTarget);
        setExecutionStatus(execution.status);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setError(cause instanceof Error ? cause.message : "Failed to load project.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    void workspaceMembersFetch
      .then((availableMembers) => {
        if (!active) return;
        setWorkspaceMembers(availableMembers);
        setWorkspaceMembersError(null);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setWorkspaceMembersError(
          cause instanceof Error ? cause.message : "Workspace people could not be loaded.",
        );
      })
      .finally(() => {
        if (active) setWorkspaceMembersLoading(false);
      });

    void agentMembersFetch
      .then((canonical) => {
        if (!active || agentMembersReadGenerationRef.current !== agentMembersReadGeneration) return;
        setAgentMembers(canonical);
        setAgentMembersError(null);
      })
      .catch((cause: unknown) => {
        if (!active || agentMembersReadGenerationRef.current !== agentMembersReadGeneration) return;
        setAgentMembersError(
          cause instanceof Error ? cause.message : "Failed to load Agent Members.",
        );
      })
      .finally(() => {
        if (active && agentMembersReadGenerationRef.current === agentMembersReadGeneration) {
          setAgentMembersLoading(false);
        }
      });

    void agentEventsFetch
      .then((events) => {
        if (!active || agentMembersReadGenerationRef.current !== agentMembersReadGeneration) return;
        setAgentEvents(events);
        setAgentEventsError(null);
      })
      .catch((cause: unknown) => {
        if (!active || agentMembersReadGenerationRef.current !== agentMembersReadGeneration) return;
        setAgentEventsError(
          cause instanceof Error ? cause.message : "Agent activity history is unavailable.",
        );
      })
      .finally(() => {
        if (active && agentMembersReadGenerationRef.current === agentMembersReadGeneration) {
          setAgentEventsLoading(false);
        }
      });

    void updatesFetch
      .then((page) => {
        if (!active) return;
        setUpdates(page.updates);
        setUpdatesTotal(page.pagination.total);
        setUpdatesPage(page.pagination.page);
        setUpdatesHasMore(page.pagination.hasMore);
      })
      .catch((cause: unknown) => {
        if (!active) return;
        setUpdatesError(cause instanceof Error ? cause.message : "Failed to load project updates.");
      })
      .finally(() => {
        if (active) setUpdatesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [projectId, nonce]);

  return {
    project,
    workspace,
    repository,
    members,
    workspaceMembers,
    workspaceMembersLoading,
    workspaceMembersError,
    agentMembers,
    agentMembersLoading,
    agentMembersError,
    agentEvents,
    agentEventsLoading,
    agentEventsError,
    updates,
    updatesTotal,
    updatesHasMore,
    updatesLoading,
    updatesError,
    forgeTarget,
    executionStatus,
    loading,
    error,
    reload,
    addProjectMember,
    createAgentMember,
    renameAgentMember,
    retireAgentMember,
    updateAgentMemberAvatar,
    listAgentMemberInstances,
    previewAgentMemberConsolidation,
    applyAgentMemberConsolidation,
    updateSettings,
    updateDescription,
    updateWorkItemPrefix,
    updateRepository,
    addUpdate,
    updateUpdate,
    deleteUpdate,
    loadMoreUpdates,
  };
}
